import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";

import { ShopSort } from "./ShopSort";
import { ShopItems } from "./ShopItems";
import { ShopItemsList } from "./ShopItemsList";
import { ShopCenterPagination } from "./ShopCenterPagination";
import { ShopCart } from "./ShopCart";
import { ShopCategories } from "./ShopCategories";
import { EmptyState } from "../common/EmptyState";
import { ProductGridSkeleton } from "../common/Skeletons";

export const ShopCenter = (props) => {
  const [listView, setListView] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Prop name is inverted in legacy code: TRUE means "loaded".
  const isLoading = !props.loadingProducts;
  const hasSearch = Boolean(props.search && props.search.length > 0);
  const items = props.products?.result || [];
  const hasResults = items.length > 0;

  const activeFilterCount = props.initialFilter
    ? ["categories", "brand", "radius", "wall", "diameter"].reduce(
        (sum, k) => sum + ((props.initialFilter[k] || []).length || 0),
        0
      )
    : 0;

  useEffect(() => {
    props.setShowItem(false);
  }, []);

  const handleViewChange = (_e, next) => {
    if (next === null) return;
    setListView(next === "list");
    props.setShowItem(false);
  };

  const clearSearch = () => {
    if (props.onSearchChange) props.onSearchChange("");
  };

  // Three-region layout (filters | products | basket).
  // Each region toggles independently. Build template-columns dynamically.
  const gridTemplate = () => {
    const cols = [];
    if (showFilters) cols.push("260px");
    cols.push("minmax(0, 1fr)");
    if (showCart) cols.push("360px");
    return cols.join(" ");
  };

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl" disableGutters>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
          Shop
        </Typography>

        {!props.showItem && (
          <>
            {/* Toolbar — wraps to a second row when there isn't enough width */}
            <Box
              sx={{
                p: 1.5,
                mb: 3,
                bgcolor: "background.paper",
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <Button
                variant={showFilters ? "contained" : "outlined"}
                color="primary"
                startIcon={<TuneOutlinedIcon />}
                onClick={() => setShowFilters((s) => !s)}
                sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
              >
                Filters
                {activeFilterCount > 0 && ` (${activeFilterCount})`}
              </Button>

              <Box sx={{ flex: "1 1 320px", minWidth: 240 }}>
                <ShopSort
                  onSearchChange={props.onSearchChange}
                  search={props.search}
                  onSortChange={props.onSortChange}
                  onPageSizeChange={props.onPageSizeChange}
                />
              </Box>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexShrink: 0, ml: "auto" }}
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={listView ? "list" : "grid"}
                  onChange={handleViewChange}
                  aria-label="View mode"
                >
                  <ToggleButton value="grid" aria-label="Grid view">
                    <GridViewOutlinedIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="list" aria-label="List view">
                    <ViewListOutlinedIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button
                  variant={showCart ? "contained" : "outlined"}
                  color="primary"
                  startIcon={<ShoppingCartOutlinedIcon />}
                  onClick={() => setShowCart((s) => !s)}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  Basket
                </Button>
              </Stack>
            </Box>

            {/* Pagination */}
            {!isLoading && hasResults && (
              <Box sx={{ mb: 3 }}>
                <ShopCenterPagination
                  totPages={props.pages.length}
                  currentPage={props.currentPage}
                  pageClicked={(ele) => props.onPageChange(ele)}
                />
              </Box>
            )}

            {/* Body: filters | products | basket */}
            <Box
              sx={{
                display: "grid",
                gap: 3,
                gridTemplateColumns: { xs: "1fr", md: gridTemplate() },
                alignItems: "start",
              }}
            >
              {showFilters && (
                <Box sx={{ minWidth: 0 }}>
                  <ShopCategories
                    updateFilter={props.updateFilter}
                    initialFilter={props.initialFilter}
                  />
                </Box>
              )}

              <Box sx={{ minWidth: 0 }}>
                {isLoading ? (
                  <ProductGridSkeleton count={listView ? 6 : 8} />
                ) : !hasResults ? (
                  hasSearch ? (
                    <EmptyState
                      icon={<SearchOffOutlinedIcon />}
                      title="No products match your search"
                      description={`We couldn't find any products matching "${props.search}". Try a different keyword or reset the search.`}
                      action={
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<RestartAltOutlinedIcon />}
                          onClick={clearSearch}
                        >
                          Clear search
                        </Button>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={<InventoryOutlinedIcon />}
                      title="No products available right now"
                      description="The catalogue is currently empty. New stock arrives regularly — please check back soon or contact your account manager."
                    />
                  )
                ) : !listView ? (
                  <ShopItems
                    updateCartItemCount={props.updateCartItemCount}
                    products={props.products}
                  />
                ) : (
                  <ShopItemsList
                    updateCartItemCount={props.updateCartItemCount}
                    products={props.products}
                    showItem={props.showItem}
                    setShowItem={props.setShowItem}
                  />
                )}

                {!isLoading && hasResults && (
                  <Box sx={{ mt: 3 }}>
                    <ShopCenterPagination
                      totPages={props.pages.length}
                      currentPage={props.currentPage}
                      pageClicked={(ele) => props.onPageChange(ele)}
                    />
                  </Box>
                )}
              </Box>

              {showCart && (
                <Box sx={{ minWidth: 0 }}>
                  <ShopCart showCart={showCart} setShowCart={setShowCart} />
                </Box>
              )}
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
};
